import type { LessonsBulkDeleteResult } from '../../model/lessons/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { LessonsBulkDeleteMode } from '../../model/lessons/enums'

interface LessonsBulkDestroyApiOptions {
  disciplineId: string | number
  mode: LessonsBulkDeleteMode
  /** The ticked lessons; only for BY_IDS. */
  ids?: number[]
  onStart?: () => void
  onFinish?: () => void
}

/** `DELETE /admin-api/disciplines/{discipline}/lessons` — deletes what it can and reports every lesson it skipped,
 *  so the toast names each skipped lesson with its reason instead of one error. */
export const useLessonsBulkDestroyApi = async (
  options: LessonsBulkDestroyApiOptions,
): Promise<LessonsBulkDeleteResult | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  const url = useAdminRoutes('admin.lessons.api.index', { discipline_id: options.disciplineId })

  options.onStart?.()

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.delete<ApiResponse>(url, {
      data: options.mode === LessonsBulkDeleteMode.ByIds
        ? { mode: options.mode, ids: options.ids ?? [] }
        : { mode: options.mode },
    })

    const result: LessonsBulkDeleteResult = {
      deleted: data.data?.deleted ?? [],
      skipped: data.data?.skipped ?? [],
    }

    if (result.deleted.length) {
      toast.success({
        title: 'Successfully deleted!',
        description: `Deleted lessons: ${result.deleted.length}.`,
      })
    }

    if (result.skipped.length) {
      toast.warning({
        title: 'Some lessons were not deleted',
        description: result.skipped.map(item => `#${item.id}: ${item.message}`).join(' '),
      })
    }

    if (!result.deleted.length && !result.skipped.length) {
      toast.info('There were no lessons to delete.')
    }

    return result
  } catch (err: unknown) {
    toast.error({
      title: 'Error on bulk delete!',
      description: getAxiosErrorMessage(err),
    })

    return null
  } finally {
    options.onFinish?.()
  }
}
